// Removed framer-motion for strict batch compliance
import useSWR from 'swr';
import { useAuthStore } from '../../hooks/useAuth';
import { useDockDetails } from '../../hooks/useDockDetails';
import { format } from 'date-fns';
import { formatWeight } from '../../utils/units';

interface DockDetailPanelProps {
    dockId: string | null;
    onClose: () => void;
}

const PANEL_EVENT_LABELS: Record<string, string> = {
    note: 'Exception noted',
    assign: 'Exception assigned',
    resolve: 'Exception resolved',
    reason_change: 'Reason updated',
};

const PANEL_EVENT_COLORS: Record<string, { dot: string; text: string }> = {
    note: { dot: 'bg-amber-500', text: 'text-amber-400' },
    assign: { dot: 'bg-blue-500', text: 'text-blue-400' },
    resolve: { dot: 'bg-emerald-500', text: 'text-emerald-400' },
    reason_change: { dot: 'bg-amber-500', text: 'text-amber-400' },
};

const PANEL_DEFAULT_COLOR = { dot: 'bg-slate-500', text: 'text-slate-400' };

const panelEventFetcher = async ([url, token]: [string, string]) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Failed to fetch events');
    return res.json();
};

export function DockDetailPanel({ dockId, onClose }: DockDetailPanelProps) {
    const { token } = useAuthStore();
    const { details, isLoading } = useDockDetails(dockId);

    // Fetch recent dock exception events (must be before any early returns — rules of hooks)
    const { data: eventsData } = useSWR(
        dockId && token ? [`/api/docks/${dockId}/exception/events`, token] : null,
        panelEventFetcher,
        { revalidateOnFocus: false }
    );
    const recentEvents = (eventsData?.events || []).slice(0, 5);

    if (!dockId) return null;

    // Loading State
    if (isLoading) {
        return (
            <>
                <div className="fixed inset-0 bg-slate-950 z-40" onClick={onClose} />
                <div className="fixed right-0 top-0 h-screen w-[500px] bg-slate-900 border-l border-slate-800 shadow-2xl z-50 flex items-center justify-center font-sans text-slate-100">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-cyan-900 border-t-cyan-500 rounded-full animate-spin" />
                        <p className="text-cyan-500 font-mono text-sm">ESTABLISHING UPLINK...</p>
                    </div>
                </div>
            </>
        );
    }

    const appt = details?.appointment;
    const dock = details?.dock;

    return (
        <>
            {/* Backdrop - Solid scrim for strict compliance */}
            <div
                onClick={onClose}
                className="fixed inset-0 bg-slate-950 z-40"
            />

            {/* Panel - Solid Slate-900, no blur */}
            <div className="fixed right-0 top-0 h-screen w-[500px] bg-slate-900 border-l border-slate-800 shadow-2xl z-50 flex flex-col font-sans text-slate-100 transform transition-transform duration-300 ease-in-out">
                {/* Header */}
                <div className="p-6 border-b border-slate-800 relative overflow-hidden bg-slate-900">
                    {/* Removed ambient blur orb for strictness */}

                    <div className="flex justify-between items-start mb-6 relative z-10">
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <span className={`${appt ? 'text-cyan-400' : 'text-purple-400'} font-bold uppercase text-xs tracking-[0.2em]`}>
                                    {appt ? 'Active Mission' : 'Dock Vacant'}
                                </span>
                            </div>
                            <h2 className="text-5xl font-mono font-bold text-white tracking-tight mt-2">{dock?.dockNumber || dockId}</h2>
                        </div>
                        <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">✕</button>
                    </div>

                    {/* Key Metrics or Vacant Pulse */}
                    {appt ? (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
                                <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Check-In Time</div>
                                <div className="font-mono text-xl font-bold text-emerald-400">
                                    {format(new Date(appt.checkInTime), 'HH:mm')}
                                </div>
                            </div>
                            <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
                                <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Status</div>
                                <div className="font-bold text-white text-lg capitalize">{appt.status.replace('_', ' ')}</div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 bg-slate-800 border border-slate-700 rounded-lg">
                            <p className="text-slate-400 font-bold text-center uppercase tracking-widest text-sm">
                                Ready for Assignment
                            </p>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="flex-grow overflow-y-auto p-6 space-y-8 custom-scrollbar bg-slate-900">
                    {appt ? (
                        <>
                            {/* Carrier Card */}
                            <section>
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <span className="w-1 h-3 bg-cyan-500 rounded-full"></span>
                                    Logistics Partner
                                </h3>
                                <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 relative overflow-hidden">
                                    <div className="grid grid-cols-2 gap-y-6">
                                        <div className="col-span-2">
                                            <div className="text-[10px] text-slate-400 font-semibold mb-1">CARRIER COMPANY</div>
                                            <div className="text-white font-bold text-lg">{appt.carrierName}</div>
                                            <div className="font-mono text-xs text-cyan-400 mt-0.5">{appt.carrierMcNumber}</div>
                                        </div>
                                        {appt.driverName && (
                                            <div>
                                                <div className="text-[10px] text-slate-400 font-semibold mb-1">DRIVER</div>
                                                <div className="text-slate-200 text-sm">{appt.driverName}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </section>

                            {/* Cargo & POs */}
                            <section>
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <span className="w-1 h-3 bg-indigo-500 rounded-full"></span>
                                    Manifest
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex flex-wrap gap-2">
                                        {details?.pos.map(po => (
                                            <div key={po.customerPoNumber} className="bg-slate-800 border border-slate-700 rounded p-3 w-full">
                                                <span className="font-mono text-xs font-bold text-slate-300 block mb-2">
                                                    {po.customerPoNumber}
                                                </span>
                                                <div className="flex justify-between text-xs text-slate-400">
                                                    <span>{po.totalSkids} Skids</span>
                                                    <span>{formatWeight(po.totalWeight)}</span>
                                                </div>
                                            </div>
                                        ))}
                                        {details?.pos.length === 0 && (
                                            <div className="text-slate-500 italic text-sm">No PO Details Linked</div>
                                        )}
                                    </div>
                                </div>
                            </section>
                        </>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-slate-600">
                            <div className="text-6xl mb-4 grayscale">🚛</div>
                            <p className="text-slate-500 font-mono text-sm">NO ACTIVE VEHICLE</p>
                        </div>
                    )}

                    {/* Recent Dock Activity — from existing dock_exception_events */}
                    <section>
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <span className="w-1 h-3 bg-amber-500 rounded-full"></span>
                            Recent Dock Activity
                        </h3>
                        {recentEvents.length === 0 ? (
                            <p className="text-xs text-slate-500 italic">No recent dock activity</p>
                        ) : (
                            <div className="relative pl-4">
                                <div className="absolute left-[5px] top-1 bottom-1 w-px bg-slate-700/60" />
                                <div className="space-y-2.5">
                                    {recentEvents.map((ev: any) => {
                                        const colors = PANEL_EVENT_COLORS[ev.type] || PANEL_DEFAULT_COLOR;
                                        const label = PANEL_EVENT_LABELS[ev.type] || ev.type;
                                        const actorName = ev.createdBy?.firstName
                                            ? `${ev.createdBy.firstName}${ev.createdBy.lastName ? ' ' + ev.createdBy.lastName.charAt(0) + '.' : ''}`
                                            : null;
                                        const detail = ev.payload?.reason || ev.payload?.note || null;
                                        const ts = ev.createdAt
                                            ? new Date(ev.createdAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' })
                                            : null;
                                        return (
                                            <div key={ev.id} className="relative flex items-start gap-2.5">
                                                <div className={`w-2 h-2 rounded-full ${colors.dot} shrink-0 mt-[5px] -ml-[3px] ring-2 ring-slate-900 z-10`} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-baseline gap-2 flex-wrap">
                                                        <span className={`text-xs font-semibold ${colors.text}`}>{label}</span>
                                                        {detail && <span className="text-[10px] text-slate-500 truncate max-w-[160px]">{detail}</span>}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        {ts && <span className="text-[10px] text-slate-500 tabular-nums">{ts}</span>}
                                                        {actorName && <span className="text-[10px] text-slate-600">· {actorName}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </section>
                </div>

                {/* Action Bar */}
                {appt && (
                    <div className="p-6 border-t border-slate-800 bg-slate-900 grid grid-cols-2 gap-3 mt-auto">
                        <button className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold text-slate-300 bg-slate-800 hover:bg-slate-700 border border-slate-700 transition-all">
                            ⚠ Flag Delay
                        </button>
                        <button className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-500 shadow-sm transition-all">
                            Release Dock
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
